import { Bell, CheckCircle2, X, XCircle } from 'lucide-react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'
import { registerToastListener, type ToastItem } from '../lib/toast'
import { cn } from '../lib/utils'

const TOAST_DURATION_MS = 4000

type ToastContextValue = {
  show: (toast: ToastItem) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

type ToastProviderProps = {
  children: ReactNode
}

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ToastItem
  onDismiss: (id: string) => void
}) {
  const isSuccess = toast.type === 'success'
  const isError = toast.type === 'error'
  const Icon = isSuccess ? CheckCircle2 : isError ? XCircle : Bell

  return (
    <div
      role="status"
      className={cn(
        'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border px-4 py-3 shadow-lg',
        isError
          ? 'border-danger/20 bg-white text-ink-900'
          : 'border-green-200 bg-white text-ink-900',
      )}
    >
      <Icon
        className={cn(
          'mt-0.5 h-5 w-5 shrink-0',
          isError ? 'text-danger' : 'text-green-600',
        )}
      />
      <p className="flex-1 text-sm leading-snug">{toast.message}</p>
      <button
        type="button"
        aria-label="Dismiss Notification"
        className="rounded p-0.5 text-ink-400 hover:text-ink-700"
        onClick={() => onDismiss(toast.id)}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const location = useLocation()

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  useEffect(() => {
    setToasts([])
  }, [location.key])

  const show = useCallback(
    (toast: ToastItem) => {
      setToasts((current) => [...current, toast])

      window.setTimeout(() => {
        dismiss(toast.id)
      }, TOAST_DURATION_MS)
    },
    [dismiss],
  )

  useEffect(() => {
    registerToastListener(show)

    return () => registerToastListener(null)
  }, [show])

  const value = useMemo<ToastContextValue>(() => ({ show }), [show])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="pointer-events-none fixed right-4 bottom-4 z-[90] flex max-w-sm flex-col gap-2"
      >
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)

  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }

  return context
}
